/**
 * Mobility MVP API Client
 * Functions for mobility assessment operations
 */

import { edgeInvoke } from '@/lib/edgeInvoke';

export interface MobilityQuizAnswers {
  posterior_chain: 'yes' | 'no' | 'not_sure';
  thoracic_rotation: 'yes' | 'no' | 'not_sure';
  hip_mobility: 'yes' | 'no' | 'not_sure';
  shoulder_mobility: 'yes' | 'no' | 'not_sure';
  injuries: string[]; // Array of injury types
}

export interface MobilityProfile {
  user_id: string;
  posterior_chain: 'good' | 'limited' | null;
  thoracic_rotation: 'good' | 'limited' | null;
  hip_mobility: 'good' | 'limited' | null;
  shoulder_mobility: 'good' | 'limited' | null;
  injury_flags: string[];
  pelvic_control: 'good_control' | 'limited_control' | 'no_control' | null;
  created_at: string;
  updated_at: string;
}

export interface SubmitQuizResponse {
  success: boolean;
  profile: MobilityProfile;
  message: string;
}

export interface PelvicTiltResponse {
  success: boolean;
  pelvic_control: 'good_control' | 'limited_control' | 'no_control';
  evidence: string[];
  confidence: number;
  profile: MobilityProfile;
  message: string;
}

export interface MobilityProfileResponse {
  profile: MobilityProfile | null;
  last_updated: string | null;
  version: string | null;
  recommended_retest: string | null;
  pelvic_tilt_last_run?: {
    id: string;
    created_at: string;
    cf_stream_uid: string;
    pelvic_control: 'good_control' | 'limited_control' | 'no_control' | null;
    confidence: number | null;
    evidence: string[] | null;
    pose_fps: number | null;
    llm_model: string | null;
  } | null;
  has_profile: boolean;
}

/**
 * Submit mobility questionnaire answers
 */
export async function submitMobilityQuiz(
  answers: MobilityQuizAnswers
): Promise<SubmitQuizResponse> {
  const response = await edgeInvoke<SubmitQuizResponse>('mobility-submit-quiz', {
    answers
  });
  return response;
}

/**
 * Upload pelvic tilt video and get assessment
 */
export async function uploadPelvicTiltVideo(
  playbackId: string,
  cfStreamUid?: string
): Promise<PelvicTiltResponse> {
  const response = await edgeInvoke<PelvicTiltResponse>('mobility-upload-pelvic-video', {
    playback_id: playbackId,
    cf_stream_uid: cfStreamUid || playbackId
  });
  return response;
}

/**
 * Get user's mobility profile
 */
export async function getMobilityProfile(): Promise<MobilityProfileResponse> {
  const response = await edgeInvoke<MobilityProfileResponse>('mobility-profile', {});
  return response;
}


